// Decode HTML entities in plain text pulled from third-party sources.
//
// Gmail's `message.snippet` arrives HTML-entity-encoded (e.g. `I&#39;d`,
// `you&#39;ll`, `&quot;`, `&amp;`). The gmail mapper stores it verbatim and the
// UI renders it as text, so React shows the literal `&#39;` to the user. This
// decodes the common named + numeric (decimal and hex) entities for display.
//
// Pure + jsdom-safe (no DOMParser / textarea trick) so it is unit-testable and
// SSR-safe. Single-pass by design: Gmail snippets are single-encoded, and a
// single pass avoids turning a literal `&amp;#39;` into `'` (which would be
// wrong for genuinely double-encoded input we don't expect here).

const NAMED: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

export function decodeHtmlEntities(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string): string => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const digits = isHex ? body.slice(2) : body.slice(1);
      const code = Number.parseInt(digits, isHex ? 16 : 10);
      if (Number.isNaN(code) || code < 0 || code > 0x10ffff) return match;
      try {
        return String.fromCodePoint(code);
      } catch {
        return match;
      }
    }
    const named = NAMED[body.toLowerCase()];
    return named ?? match;
  });
}
